import React, { useState } from 'react';
import TabelaCrud from '../components/TabelaCrud';
import { useCreches } from '../hooks/useCreches';
import api, { getCurrentTenantId } from '../services/api';
import { formatarDataBR, hojeISO, paraISO, semanaDe } from '../common/data';

interface Cardapio {
  id: number;
  // A coluna é `id_creche`. O alias `creche_id` que esta tela usava era
  // DESCARTADO pelo `enforceTenant` no backend (ver tenant.service.ts) — com
  // isso, o gestor de sistema sem creche selecionada tomava 500 ao salvar, e a
  // coluna "Creche" da listagem mostrava "ID undefined". Mesmo bug dos PRs
  // #79/#100/#101.
  id_creche: number;
  dia_semana: string;
  data: string;
  cafe_manha?: string;
  lanche_manha?: string;
  almoco?: string;
  lanche_tarde?: string;
  jantar?: string;
  observacoes?: string;
  id_turma?: number;
}

interface Risco {
  id_aluno: number;
  nome: string;
  descricao: string;
  gravidade: string;
  origem: string;
  termos: string[];
  refeicoes: string[];
}

const ORDEM_DIAS = ['segunda', 'terca', 'quarta', 'quinta', 'sexta', 'sabado', 'domingo'];

/**
 * A ficha da criança guarda alergia como texto livre, sem gravidade — só o
 * registro do módulo Saúde tem esse campo. Mostrar "NAO_INFORMADA" faria o
 * sistema parecer estar afirmando algo sobre a gravidade; o correto é dizer que
 * ninguém classificou, e sugerir onde classificar.
 */
const GRAVIDADE_LABEL: Record<string, string> = {
  grave: 'grave',
  moderada: 'moderada',
  moderado: 'moderada',
  leve: 'leve',
  nao_informada: 'gravidade não classificada',
};

const DIAS_LABEL: Record<string, string> = {
  segunda: 'Segunda-feira',
  terca: 'Terça-feira',
  quarta: 'Quarta-feira',
  quinta: 'Quinta-feira',
  sexta: 'Sexta-feira',
  sabado: 'Sábado',
  domingo: 'Domingo',
};

export default function CardapiosPage() {
  const { options, creches } = useCreches();
  const tenantScoped = Boolean(getCurrentTenantId());

  const [semanaRef, setSemanaRef] = useState(hojeISO());
  const [riscos, setRiscos] = useState<{ carregando: boolean; dados: any | null }>({
    carregando: false, dados: null,
  });

  function nomeCrecheById(id?: number) {
    if (!id) return '—';
    return creches.find((c) => c.id === id)?.nome || `ID ${id}`;
  }

  function escaparHtml(texto?: string) {
    if (!texto) return '';
    return texto
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  function resumoDia(c: Cardapio) {
    const itens = [c.cafe_manha, c.lanche_manha, c.almoco, c.lanche_tarde, c.jantar]
      .filter((t) => !!t && t.trim().length > 0)
      .length;
    return `${itens}/5 refeições preenchidas`;
  }

  /**
   * Confere o cardápio contra as alergias e restrições das crianças.
   *
   * O gestor escreve o cardápio; quem sabe que a Ana não pode ovo é a ficha de
   * saúde. Cruzar isso à mão, todo dia, para vinte crianças, ninguém faz.
   */
  async function conferirRiscos(id: number) {
    setRiscos({ carregando: true, dados: null });
    try {
      const { data } = await api.get(`/nutricao/cardapios/${id}/riscos`);
      setRiscos({ carregando: false, dados: data });
    } catch {
      setRiscos({ carregando: false, dados: { erro: 'Não foi possível conferir as restrições alimentares.' } });
    }
  }

  /**
   * PDF da SEMANA — não de tudo que existe.
   *
   * A versão anterior puxava todos os cardápios cadastrados, ordenava por dia da
   * semana e chamava o resultado de "Cardápio Semanal": com dois meses no ar, o
   * documento entregue aos pais misturava semanas diferentes sob o mesmo título.
   */
  async function gerarDocumentoPais() {
    try {
      const { inicio, fim } = semanaDe(semanaRef);
      const { data } = await api.get<Cardapio[]>('/cardapios');

      const itens = data
        .filter((c) => {
          const d = paraISO(c.data);
          return d >= inicio && d <= fim;
        })
        .sort((a, b) => {
          const porData = paraISO(a.data).localeCompare(paraISO(b.data));
          return porData !== 0 ? porData : ORDEM_DIAS.indexOf(a.dia_semana) - ORDEM_DIAS.indexOf(b.dia_semana);
        });

      if (!itens.length) {
        window.alert(
          `Nenhum cardápio cadastrado para a semana de ${formatarDataBR(inicio)} a ${formatarDataBR(fim)}.`,
        );
        return;
      }

      const nomeCreche = nomeCrecheById(itens[0].id_creche);
      const hoje = formatarDataBR(hojeISO());

      const secoes = itens.map((c) => `
        <section class="dia-cardapio">
          <div class="dia-topo">
            <h2>${DIAS_LABEL[c.dia_semana] || c.dia_semana}</h2>
            <span class="dia-data">${formatarDataBR(c.data)}</span>
          </div>
          <div class="grade-refeicoes">
            <article class="bloco-refeicao bloco-cafe">
              <h3>Café da manhã</h3>
              <p>${escaparHtml(c.cafe_manha) || '-'}</p>
            </article>
            <article class="bloco-refeicao bloco-lanche-manha">
              <h3>Lanche da manhã</h3>
              <p>${escaparHtml(c.lanche_manha) || '-'}</p>
            </article>
            <article class="bloco-refeicao bloco-almoco">
              <h3>Almoço</h3>
              <p>${escaparHtml(c.almoco) || '-'}</p>
            </article>
            <article class="bloco-refeicao bloco-lanche-tarde">
              <h3>Lanche da tarde</h3>
              <p>${escaparHtml(c.lanche_tarde) || '-'}</p>
            </article>
            <article class="bloco-refeicao bloco-jantar">
              <h3>Jantar</h3>
              <p>${escaparHtml(c.jantar) || '-'}</p>
            </article>
          </div>
          ${c.observacoes ? `<p class="obs"><strong>Observações da equipe:</strong> ${escaparHtml(c.observacoes)}</p>` : ''}
        </section>
      `).join('');

      const html = `
      <!doctype html>
      <html lang="pt-BR">
      <head>
        <meta charset="UTF-8" />
        <title>Cardápio Semanal - ${escaparHtml(nomeCreche)}</title>
        <style>
          @page { size: A4; margin: 14mm; }
          * { box-sizing: border-box; }
          body { font-family: Arial, sans-serif; color: #1f2937; margin: 0; background: #f8fafc; }
          .cabecalho {
            border: 1px solid #dbeafe;
            background: linear-gradient(120deg, #eff6ff, #ecfeff);
            border-radius: 10px;
            margin-bottom: 12px;
            padding: 12px;
          }
          h1 { margin: 0 0 4px; color: #0f766e; font-size: 22px; }
          .meta { font-size: 12px; color: #334155; }
          .aviso {
            margin-top: 6px;
            font-size: 11px;
            color: #0f172a;
            background: #fef9c3;
            border: 1px solid #fde68a;
            border-radius: 8px;
            padding: 6px 8px;
          }
          .dia-cardapio {
            margin: 0 0 10px;
            page-break-inside: avoid;
            border: 1px solid #e2e8f0;
            border-radius: 10px;
            background: #fff;
            padding: 10px;
          }
          .dia-topo {
            display: flex;
            align-items: center;
            justify-content: space-between;
            margin-bottom: 8px;
          }
          .dia-topo h2 { margin: 0; font-size: 16px; color: #0f172a; }
          .dia-data { font-size: 11px; color: #475569; background: #f1f5f9; border-radius: 999px; padding: 3px 8px; }
          .grade-refeicoes {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 8px;
          }
          .bloco-refeicao {
            border-radius: 8px;
            border: 1px solid #e2e8f0;
            padding: 8px;
            min-height: 70px;
          }
          .bloco-refeicao h3 { margin: 0 0 4px; font-size: 12px; color: #0f172a; }
          .bloco-refeicao p { margin: 0; font-size: 11px; line-height: 1.45; color: #334155; }
          .bloco-cafe { background: #fff7ed; }
          .bloco-lanche-manha { background: #fef9c3; }
          .bloco-almoco { background: #ecfccb; }
          .bloco-lanche-tarde { background: #e0f2fe; }
          .bloco-jantar { background: #ede9fe; grid-column: span 2; }
          .obs {
            background: #f8fafc;
            border: 1px solid #e2e8f0;
            border-radius: 8px;
            padding: 7px 8px;
            margin: 8px 0 0;
            font-size: 11px;
            color: #334155;
          }
          .rodape { margin-top: 10px; font-size: 10px; color: #64748b; }
          @media print { .nao-imprimir { display: none; } }
          .nao-imprimir { margin-top: 12px; display: flex; gap: 8px; }
          .btn { border: none; padding: 8px 12px; border-radius: 6px; cursor: pointer; }
          .btn-print { background: #0f766e; color: #fff; }
          .btn-close { background: #e5e7eb; color: #111827; }
        </style>
      </head>
      <body>
        <div class="cabecalho">
          <h1>Cardápio Semanal</h1>
          <div class="meta">Creche: ${escaparHtml(nomeCreche)}</div>
          <div class="meta">Semana de ${formatarDataBR(inicio)} a ${formatarDataBR(fim)}</div>
          <div class="meta">Documento para pais e responsáveis - Emitido em ${hoje}</div>
          <div class="aviso">Este cardápio pode ser ajustado conforme orientação nutricional e necessidade alimentar individual.</div>
        </div>
        ${secoes}
        <div class="rodape">Este documento pode ser exibido em tela e impresso em formato A4.</div>
        <div class="nao-imprimir">
          <button class="btn btn-print" onclick="window.print()">Imprimir / Salvar em PDF</button>
          <button class="btn btn-close" onclick="window.close()">Fechar</button>
        </div>
      </body>
      </html>
      `;

      const w = window.open('', '_blank');
      if (!w) {
        window.alert('Não foi possível abrir a visualização do PDF. Verifique o bloqueador de pop-up.');
        return;
      }
      w.document.open();
      w.document.write(html);
      w.document.close();
    } catch {
      window.alert('Erro ao gerar documento de cardápios.');
    }
  }

  const semana = semanaDe(semanaRef);

  return (
    <div className="cardapio-page">
      <section className="cardapio-orientacao">
        <h3>Planejamento Semanal de Alimentação</h3>
        <p>
          Estruture o cardápio por dia da semana com as cinco refeições da rotina da creche.
          O documento gerado fica adequado para compartilhar com pais e responsáveis.
        </p>
      </section>

      {riscos.carregando && <div className="aviso-carregando">Conferindo restrições alimentares…</div>}

      {riscos.dados && (
        <section className={`cardapio-riscos ${riscos.dados.total > 0 ? 'com-risco' : 'sem-risco'}`}>
          <div className="riscos-topo">
            <h3>
              {riscos.dados.erro
                ? 'Não foi possível conferir'
                : riscos.dados.total > 0
                  ? `⚠️ ${riscos.dados.total} criança(s) em risco neste cardápio`
                  : '✅ Nenhuma restrição alimentar conflita com este cardápio'}
            </h3>
            <button className="btn-secundario" onClick={() => setRiscos({ carregando: false, dados: null })}>
              Fechar
            </button>
          </div>

          {riscos.dados.erro && <p className="risco-erro">{riscos.dados.erro}</p>}

          {!riscos.dados.erro && (
            <>
              <p className="riscos-escopo">
                {riscos.dados.escopo} — {riscos.dados.criancasConsideradas} criança(s) com restrição cadastrada.
              </p>

              {(riscos.dados.riscos as Risco[]).map((r, i) => (
                <div key={`${r.id_aluno}-${i}`} className={`risco-linha gravidade-${r.gravidade}`}>
                  <strong>{r.nome}</strong>
                  <span className="risco-gravidade">{GRAVIDADE_LABEL[r.gravidade] || r.gravidade}</span>
                  <div className="risco-detalhe">
                    <span>Restrição: {r.descricao}</span>
                    <span>Encontrado: <strong>{r.termos.join(', ')}</strong> em {r.refeicoes.join(', ')}</span>
                    <span className="risco-origem">{r.origem}</span>
                  </div>
                </div>
              ))}

              {/* A limitação vem junto do resultado. Quem lê "nenhum risco" como
                  garantia deixa de conferir o rótulo — e é aí que acontece. */}
              <p className="riscos-observacao">{riscos.dados.observacao}</p>
            </>
          )}
        </section>
      )}

      <TabelaCrud<Cardapio>
        titulo="Cardápios Semanais"
        endpoint="/cardapios"
        headerActions={(
          <>
            <label className="semana-pdf">
              Semana de referência
              <input
                type="date"
                value={semanaRef}
                onChange={(e) => setSemanaRef(e.target.value || hojeISO())}
              />
            </label>
            <button className="btn-secundario" onClick={gerarDocumentoPais}>
              Gerar PDF A4 para Pais ({formatarDataBR(semana.inicio)} a {formatarDataBR(semana.fim)})
            </button>
          </>
        )}
        colunas={[
          { chave: 'id', label: 'ID' },
          { chave: 'id_creche', label: 'Creche', render: (item) => nomeCrecheById(item.id_creche) },
          { chave: 'dia_semana', label: 'Dia', render: (item) => DIAS_LABEL[item.dia_semana] || item.dia_semana },
          { chave: 'data', label: 'Data', render: (item) => formatarDataBR(item.data) },
          { chave: 'almoco', label: 'Prato Principal' },
          { chave: 'observacoes', label: 'Resumo', render: (item) => resumoDia(item) },
          {
            chave: 'id_turma',
            label: 'Alergias',
            render: (item) => (
              <button className="btn-link" onClick={() => conferirRiscos(item.id)}>
                Conferir
              </button>
            ),
          },
        ]}
        itemVazio={{
          id_creche: 0,
          dia_semana: 'segunda',
          data: '',
          cafe_manha: '',
          lanche_manha: '',
          almoco: '',
          lanche_tarde: '',
          jantar: '',
          observacoes: '',
        }}
        validateItem={(item) => {
          if (!tenantScoped && (!item.id_creche || Number(item.id_creche) <= 0)) return 'Selecione a Creche do cardápio.';
          if (!item.dia_semana) return 'Selecione o dia da semana.';
          if (!item.data) return 'Informe a data do cardápio.';
          if (!item.cafe_manha?.trim()) return 'Preencha o café da manhã.';
          if (!item.lanche_manha?.trim()) return 'Preencha o lanche da manhã.';
          if (!item.almoco?.trim()) return 'Preencha o almoço.';
          if (!item.lanche_tarde?.trim()) return 'Preencha o lanche da tarde.';
          if (!item.jantar?.trim()) return 'Preencha o jantar.';
          return null;
        }}
        renderFormulario={(item, onChange) => (
          <>
            <div className="cardapio-form-head">
              <span className="cardapio-pill">Cardápio Diário</span>
              <p>Preencha todas as refeições para manter o informativo completo.</p>
            </div>

            <label className={tenantScoped ? 'campo-tenant-oculto' : undefined}>Creche
              <select value={item.id_creche ?? 0} onChange={(e) => onChange('id_creche', Number(e.target.value))}>
                <option value={0}>Selecione...</option>
                {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </label>

            <div className="linha-campos linha-2">
              <label>Data
                <input type="date" value={paraISO(item.data) || ''} onChange={(e) => onChange('data', e.target.value)} />
              </label>
              <label>Dia da semana
                <select value={item.dia_semana ?? 'segunda'} onChange={(e) => onChange('dia_semana', e.target.value)}>
                  {ORDEM_DIAS.map((d) => <option key={d} value={d}>{DIAS_LABEL[d]}</option>)}
                </select>
              </label>
            </div>

            <div className="cardapio-refeicoes-grid">
              <label>Café da manhã
                <textarea value={item.cafe_manha ?? ''} rows={2} onChange={(e) => onChange('cafe_manha', e.target.value)} />
              </label>
              <label>Lanche da manhã
                <textarea value={item.lanche_manha ?? ''} rows={2} onChange={(e) => onChange('lanche_manha', e.target.value)} />
              </label>
              <label>Almoço
                <textarea value={item.almoco ?? ''} rows={2} onChange={(e) => onChange('almoco', e.target.value)} />
              </label>
              <label>Lanche da tarde
                <textarea value={item.lanche_tarde ?? ''} rows={2} onChange={(e) => onChange('lanche_tarde', e.target.value)} />
              </label>
              <label className="cardapio-jantar">Jantar
                <textarea value={item.jantar ?? ''} rows={2} onChange={(e) => onChange('jantar', e.target.value)} />
              </label>
            </div>

            <label>Observações
              <textarea value={item.observacoes ?? ''} rows={2} onChange={(e) => onChange('observacoes', e.target.value)} />
            </label>
            <label>ID da Turma (opcional — em branco vale para a unidade toda)
              <input
                type="number"
                value={item.id_turma ?? ''}
                onChange={(e) => onChange('id_turma', e.target.value ? Number(e.target.value) : null)}
              />
            </label>
          </>
        )}
      />
    </div>
  );
}
